import { type AccordionItemProps, AccordionItemTitlePosition } from '@capsicum/common';
import { type PropsWithChildren, useState } from 'react';
import styled from 'styled-components';

import { ChevronDownIcon } from '../../icons/ChevronDown';

export const AccordionItem = ({
	children,
	disabled = false,
	icon,
	isByDefaultExpanded = false,
	testID = 'accordion-item',
	title,
	titlePosition = AccordionItemTitlePosition.LEFT
}: PropsWithChildren<AccordionItemProps>) => {
	const [isExpanded, setIsExpanded] = useState(isByDefaultExpanded);
	const onClick = () => {
		if (disabled) return;
		setIsExpanded(!isExpanded);
	};

	return (
		<>
			<S.Title $disabled={disabled} data-testid={testID} role="button" onClick={onClick}>
				<S.Section $titlePosition={titlePosition}>
					<S.Label $titlePosition={titlePosition}>{title}</S.Label>
					<S.Icon $disabled={disabled} $open={isExpanded} data-testid={testID + '-icon'}>
						{icon || <ChevronDownIcon />}
					</S.Icon>
				</S.Section>
			</S.Title>
			<div>
				<S.Content $open={isExpanded} data-testid={testID + '-content'}>
					<S.ContentContainer $open={isExpanded}>{children}</S.ContentContainer>
				</S.Content>
			</div>
		</>
	);
};

namespace S {
	export const Container = styled.div`
		padding: ${({ theme }) => theme.spacings.small};
	`;

	export const Section = styled.div<{ $titlePosition: AccordionItemTitlePosition }>`
		display: flex;
		justify-content: ${({ $titlePosition }) =>
			$titlePosition === AccordionItemTitlePosition.LEFT ? 'space-between' : 'flex-end'};
		align-items: center;
		width: 100%;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const Label = styled.span<{ $titlePosition: AccordionItemTitlePosition }>`
		${({ theme }) => theme.fonts.text.text3};
		flex: ${({ $titlePosition }) => ($titlePosition === AccordionItemTitlePosition.LEFT ? 1 : 'none')};
	`;

	export const Title = styled.div<{ $disabled: boolean }>`
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		cursor: ${({ $disabled }) => ($disabled ? 'auto' : 'pointer')};
		display: flex;
		justify-content: space-between;
		align-items: center;
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const Content = styled.div<{ $open: boolean }>`
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		display: grid;
		grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
		overflow: hidden;
		transition: grid-template-rows 0.3s ease-in-out;
	`;

	export const ContentContainer = styled.div<{ $open: boolean }>`
		min-height: 0;
	`;

	export const Icon = styled.div<{ $disabled: boolean; $open: boolean }>`
		display: flex;
		justify-content: center;
		align-items: center;
		transition: transform 0.3s ease-out;
		transform: ${({ $disabled, $open }) => ($open && !$disabled ? 'rotate(180deg)' : 'rotate(0deg)')};
		opacity: ${({ $disabled, theme }) => ($disabled ? theme.opacity._40 : 1)};
	`;
}
